import { State } from '@autopylot-internal/state';
import { APIClient } from '@autopylot-internal/autopylot-api-client';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { AuthService } from '../auth/AuthService.ts';
import { OperatorState } from './OperatorState.ts';

export type IChangeEmailStep = 'enter-email' | 'enter-code' | 'confirm-merge' | 'done';

export interface IChangeEmailUIState {
    step: IChangeEmailStep;
    newEmail: string;
    code: string;
    inFlight: boolean;
    errorMessage?: string;
    errorCode?: string;
}

const initialState: IChangeEmailUIState = {
    step: 'enter-email',
    newEmail: '',
    code: '',
    inFlight: false,
    errorMessage: undefined,
    errorCode: undefined
};

const GENERIC_ERROR = 'Sorry, something went wrong. Please try again.';

export class ChangeEmailState extends State<IChangeEmailUIState> {

    static inject = () => [APIClient, TrackingService, AuthService, OperatorState];
    constructor(
        private apiClient: APIClient,
        private tracker: TrackingService,
        private authService: AuthService,
        private operatorState: OperatorState
    ) {
        super({ ...initialState });
    }

    reset = () => {
        this.setState({ ...initialState });
    };

    setNewEmail = (newEmail: string) => {
        this.setState({ newEmail, errorMessage: undefined, errorCode: undefined });
    };

    setCode = (code: string) => {
        // strip non-digits and clip to 6
        const cleaned = (code || '').replace(/\D/g, '').slice(0, 6);
        this.setState({ code: cleaned, errorMessage: undefined, errorCode: undefined });
    };

    requestCode = async (): Promise<void> => {
        const { newEmail } = this.state;
        if (!newEmail) {
            this.setState({ errorMessage: 'Please enter a new email address.', errorCode: 'newEmail.required' });
            return;
        }

        this.setState({ inFlight: true, errorMessage: undefined, errorCode: undefined });
        try {
            const entry = await this.apiClient.entry();
            await this.apiClient.post(entry.links.changeEmailRequest, { newEmail });
            this.tracker.track('Email Change Requested', () => ({ newEmail }));
            this.setState({ step: 'enter-code', code: '', inFlight: false });
        } catch (err: any) {
            this.tracker.track('Email Change Failed', () => ({ stage: 'request', errorCode: err.errorCode }));
            this.setState({
                inFlight: false,
                errorCode: err.errorCode,
                errorMessage: err.displayMessage || GENERIC_ERROR
            });
        }
    };

    resendCode = async (): Promise<void> => {
        this.setState({ inFlight: true, errorMessage: undefined, errorCode: undefined });
        try {
            const entry = await this.apiClient.entry();
            await this.apiClient.post(entry.links.changeEmailResend, undefined);
            this.tracker.track('Email Change Code Resent');
            this.setState({ code: '', inFlight: false });
        } catch (err: any) {
            this.tracker.track('Email Change Failed', () => ({ stage: 'resend', errorCode: err.errorCode }));
            this.setState({
                inFlight: false,
                errorCode: err.errorCode,
                errorMessage: err.displayMessage || GENERIC_ERROR
            });
        }
    };

    submitCode = async ({ mergeConfirmed }: { mergeConfirmed?: boolean } = {}): Promise<void> => {
        const { code } = this.state;
        if (!/^\d{6}$/.test(code)) {
            this.setState({ errorMessage: 'Enter the 6-digit code from your email', errorCode: 'code.invalid_format' });
            return;
        }

        this.setState({ inFlight: true, errorMessage: undefined, errorCode: undefined });
        try {
            const entry = await this.apiClient.entry();
            const response = await this.apiClient.post(entry.links.changeEmailVerify, { code, mergeConfirmed });

            if (response?.state === 'merge-required') {
                this.tracker.track('Email Change Code Verified', () => ({ outcome: 'merge-required' }));
                this.setState({ step: 'confirm-merge', inFlight: false });
                return;
            }

            this.tracker.track('Email Change Code Verified', () => ({ outcome: 'ok', merged: mergeConfirmed === true }));
            if (mergeConfirmed) {
                this.tracker.track('Email Change Merge Confirmed');
            }
            this.tracker.track('Email Change Completed', () => ({ newEmail: this.state.newEmail }));
            this.setState({ step: 'done', inFlight: false });
        } catch (err: any) {
            this.tracker.track('Email Change Failed', () => ({ stage: 'verify', errorCode: err.errorCode }));
            this.setState({
                inFlight: false,
                errorCode: err.errorCode,
                errorMessage: err.displayMessage || GENERIC_ERROR
            });
        }
    };

    confirmMerge = async (): Promise<void> => {
        await this.submitCode({ mergeConfirmed: true });
    };

    cancelMerge = (): void => {
        this.tracker.track('Email Change Merge Canceled');
        this.reset();
    };

    completeAndLogout = async (): Promise<void> => {
        // OperatorState is now stale (different userId/email coming after login); clear what we can locally
        this.operatorState.setState({ email: this.state.newEmail });
        await this.authService.logout();
    };
}
